import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Switch } from '../components/ui/Switch';
import { MockBadge } from '../components/MockBadge';
import {
  DateRangePicker,
  choiceCompareRange,
  type PeriodChoice,
} from '../components/stats/DateRangePicker';
import { presetRange, rangeToPeriod, todayKey } from '../components/stats/dateRange';
import { campaignHref } from '../components/CampaignCalendar';
import {
  CampaignGroupStatusBadge,
  CampaignStatusBadge,
  PendingApprovalBadge,
  isAwaitingApproval,
} from '../components/CampaignStatusBadge';
import { apiFetch } from '../lib/api';
import { groupCampaigns, splitLanguage, type CampaignGroup } from '../lib/campaignGroups';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/utils';
import {
  formatCost,
  formatMoney,
  formatNumber,
  formatPercent,
  formatRelativeDay,
  formatRoi,
  formatSendPrice,
  userName,
} from '../lib/format';
import type {
  Campaign,
  CampaignStatsListItem,
  ContactsListResponse,
  OverviewStats,
  SendingRoi,
} from '../types/api';

interface DateRange {
  /** Inclusive, epoch ms. */
  from: number;
  /** Exclusive, epoch ms. */
  to: number;
}

/** What the badges compare against, by the picker's compare mode. */
const COMPARE_LABEL: Record<PeriodChoice['compare'], string> = {
  prev: 'previous period',
  month: 'same days, month before',
  custom: 'chosen period',
  off: '',
};

/**
 * Percentage change from `previous` to `current`, or `null` when
 * `previous` is 0 — division by zero has no meaningful percentage, and
 * showing "+Infinity%" or crashing would both be worse than just omitting
 * the comparison for that KPI.
 */
function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}

function ComparisonBadge({
  current,
  previous,
  label,
}: {
  current: number;
  previous: number;
  label: string;
}) {
  const change = percentChange(current, previous);
  if (change === null) {
    return <span className="text-faint text-xs">vs. {label}: –</span>;
  }
  const isUp = change > 0;
  const isFlat = change === 0;
  return (
    <span
      className={`text-xs ${isFlat ? 'text-muted' : isUp ? 'text-emerald-600' : 'text-rose-600'}`}
    >
      {isFlat ? '·' : isUp ? '↑' : '↓'} {formatPercent(Math.abs(change))} vs. {label}
    </span>
  );
}

/** The numbers that belong together share one frame, split by hairlines. */
function KpiFrame({ cols, children }: { cols: 3 | 4; children: ReactNode }) {
  return (
    <div
      className={cn(
        'grid rounded-xl border border-border bg-card overflow-hidden',
        '[&>*]:border-border-soft',
        cols === 4
          ? 'grid-cols-2 [&>*:nth-child(even)]:border-l [&>*:nth-child(n+3)]:border-t md:grid-cols-4 md:[&>*:nth-child(n+2)]:border-l md:[&>*:nth-child(n+3)]:border-t-0'
          : 'grid-cols-1 [&>*:nth-child(n+2)]:border-t md:grid-cols-3 md:[&>*:nth-child(n+2)]:border-t-0 md:[&>*:nth-child(n+2)]:border-l',
      )}
    >
      {children}
    </div>
  );
}

function Kpi({
  label,
  value,
  children,
}: {
  label: string;
  value: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="px-[18px] py-4 min-w-0">
      <div className="text-muted text-[13px]">{label}</div>
      <div className="text-[26px] leading-tight font-semibold tracking-[-0.02em] tabular-nums my-0.5">
        {value}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** Greeting for the local time of day. */
export function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function OversigtPage() {
  const { user } = useAuth();
  const [today] = useState(() => todayKey());
  const [choice, setChoice] = useState<PeriodChoice>(() => ({
    range: presetRange('last30', today),
    compare: 'prev',
    customCompare: null,
  }));
  const [includeBots, setIncludeBots] = useState(false);

  const range = useMemo<DateRange>(() => rangeToPeriod(choice.range), [choice.range]);
  const previousRange = useMemo<DateRange | null>(() => {
    const cmp = choiceCompareRange(choice);
    return cmp ? rangeToPeriod(cmp) : null;
  }, [choice]);
  const compareLabel = COMPARE_LABEL[choice.compare];

  const campaignsQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiFetch<{ campaigns: Campaign[] }>('/campaigns'),
  });
  const statsQuery = useQuery({
    queryKey: ['stats', 'campaigns'],
    queryFn: () => apiFetch<{ items: CampaignStatsListItem[] }>('/stats/campaigns'),
  });
  const contactsQuery = useQuery({
    queryKey: ['contacts', 'active-count'],
    queryFn: () => apiFetch<ContactsListResponse>('/contacts?status=active&limit=1'),
  });
  const bots = includeBots ? 'include' : 'exclude';
  const overviewQuery = useQuery({
    queryKey: ['stats', 'overview', range.from, range.to, bots],
    queryFn: () =>
      apiFetch<OverviewStats>(`/stats/overview?from=${range.from}&to=${range.to}&bots=${bots}`),
  });
  const previousOverviewQuery = useQuery({
    queryKey: ['stats', 'overview', previousRange?.from, previousRange?.to, bots],
    queryFn: () =>
      apiFetch<OverviewStats>(
        `/stats/overview?from=${previousRange?.from}&to=${previousRange?.to}&bots=${bots}`,
      ),
    enabled: previousRange !== null,
  });

  // Sending cost, revenue from emails and ROI (26 Oct 2026): campaigns and
  // automations, counted like the Statistics page, so no bots toggle.
  const roiQuery = useQuery({
    queryKey: ['stats', 'sending-roi', range.from, range.to],
    queryFn: () => apiFetch<SendingRoi>(`/stats/sending-roi?from=${range.from}&to=${range.to}`),
  });
  const previousRoiQuery = useQuery({
    queryKey: ['stats', 'sending-roi', previousRange?.from, previousRange?.to],
    queryFn: () =>
      apiFetch<SendingRoi>(
        `/stats/sending-roi?from=${previousRange?.from}&to=${previousRange?.to}`,
      ),
    enabled: previousRange !== null,
  });

  // Flow steps belong to their automation and stay `sending` while it runs,
  // so they are left out here, as on the campaigns list.
  const campaigns = (campaignsQuery.data?.campaigns ?? []).filter((c) => c.kind !== 'flow_step');
  const pending = campaigns.filter(isAwaitingApproval);
  // Language versions of one send share a row, and archived campaigns are
  // hidden, as on the campaigns list.
  const recent = groupCampaigns(campaigns.filter((c) => c.archivedAt == null)).slice(0, 5);
  const latest = latestSend(campaigns, statsQuery.data?.items);

  const current = overviewQuery.data;
  const previous = previousOverviewQuery.data;
  const loadingRange = overviewQuery.isLoading || previousOverviewQuery.isLoading;

  const roi = roiQuery.data;
  const previousRoi = previousRoiQuery.data;
  const loadingRoi = roiQuery.isLoading || previousRoiQuery.isLoading;

  const rate = (numerator: number, denominator: number) =>
    denominator > 0 ? numerator / denominator : 0;

  const kpis = [
    {
      label: 'Delivered',
      value: current ? formatNumber(current.delivered) : '…',
      cur: current?.delivered ?? 0,
      prev: previous?.delivered ?? 0,
    },
    {
      label: 'Click rate',
      value: current ? formatPercent(rate(current.clicked, current.delivered)) : '…',
      cur: current ? rate(current.clicked, current.delivered) : 0,
      prev: previous ? rate(previous.clicked, previous.delivered) : 0,
      // What the bot switch changes, so flipping it visibly does something.
      note: current
        ? `${formatNumber(current.botClicked)} bot ${current.botClicked === 1 ? 'click' : 'clicks'} ${includeBots ? 'included' : 'left out'}`
        : undefined,
    },
    {
      label: 'Unsubscribe rate',
      value: current ? formatPercent(rate(current.unsubscribed, current.delivered)) : '…',
      cur: current ? rate(current.unsubscribed, current.delivered) : 0,
      prev: previous ? rate(previous.unsubscribed, previous.delivered) : 0,
    },
    {
      label: 'Bounce rate',
      value: current ? formatPercent(rate(current.bounced, current.sent)) : '…',
      cur: current ? rate(current.bounced, current.sent) : 0,
      prev: previous ? rate(previous.bounced, previous.sent) : 0,
    },
  ];

  return (
    <>
      <PageHeader
        title={`${greetingFor(new Date())}${userName(user) ? `, ${userName(user)}` : ''}`}
        badge={<MockBadge />}
        description={
          pending.length === 0
            ? 'No campaigns waiting for approval.'
            : pending.length === 1
              ? 'One campaign waiting for approval.'
              : `${pending.length} campaigns waiting for approval.`
        }
        actions={
          <Button asChild>
            <Link to="/kampagner">New campaign</Link>
          </Button>
        }
      />

      {pending.length > 0 && pending[0] ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl bg-warning-bg px-4 py-3.5">
          <div className="min-w-[200px] flex-1">
            <div className="flex items-center gap-2 font-semibold">
              {pending[0].name}
              <PendingApprovalBadge />
            </div>
            <div className="text-[13px] text-muted">
              {pending[0].testSendDoneAt ? 'Test email sent' : 'No test email sent yet'}
            </div>
          </div>
          <Button asChild className="no-underline">
            <Link to={`/kampagner/${pending[0].id}/send`}>Review and approve</Link>
          </Button>
        </div>
      ) : null}

      <section aria-labelledby="period-heading">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 id="period-heading" className="m-0 text-base font-semibold">
            Period overview
          </h2>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <DateRangePicker value={choice} today={today} onApply={setChoice} />
            <label className="flex items-center gap-2 ml-2 text-sm text-muted">
              <Switch
                checked={includeBots}
                onChange={() => setIncludeBots((v) => !v)}
                label="Include bot clicks"
              />
              Include bot clicks
            </label>
          </div>
        </div>
        <KpiFrame cols={4}>
          {kpis.map((k) => (
            <Kpi key={k.label} label={k.label} value={loadingRange ? '…' : k.value}>
              {!loadingRange && 'note' in k && k.note ? (
                <div className="text-muted text-[12px] mb-1">{k.note}</div>
              ) : null}
              {!loadingRange && current && previous ? (
                <ComparisonBadge current={k.cur} previous={k.prev} label={compareLabel} />
              ) : null}
            </Kpi>
          ))}
        </KpiFrame>
        <div className="mt-3" role="group" aria-label="Sending cost and return">
          <KpiFrame cols={3}>
            <Kpi
              label="Sending cost"
              value={loadingRoi ? '…' : roi ? formatCost(roi.cost, roi.currency) : '–'}
            >
              {!loadingRoi && roi ? (
                <span className="block text-faint text-xs">
                  {formatNumber(roi.sent)} emails at{' '}
                  {formatSendPrice(roi.costPerThousand, roi.currency)} per 1,000
                </span>
              ) : null}
              {!loadingRoi && roi && previousRoi ? (
                <ComparisonBadge
                  current={roi.cost}
                  previous={previousRoi.cost}
                  label={compareLabel}
                />
              ) : null}
            </Kpi>
            <Kpi
              label="Revenue from emails"
              value={
                loadingRoi ? (
                  '…'
                ) : roi ? (
                  <>
                    {formatMoney(roi.revenue, roi.currency)}
                    {roi.onHoldOrders > 0 ? (
                      <span className="text-faint font-normal text-[17px]">
                        {' '}
                        + {formatMoney(roi.onHoldRevenue, roi.currency)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  '–'
                )
              }
            >
              {!loadingRoi && roi ? (
                <span className="block text-faint text-xs">
                  {formatNumber(roi.orders)} {roi.orders === 1 ? 'order' : 'orders'}
                  {roi.onHoldOrders > 0 ? ` (+${formatNumber(roi.onHoldOrders)} on hold)` : ''}, net
                  of refunds
                </span>
              ) : null}
              {!loadingRoi && roi && previousRoi ? (
                <ComparisonBadge
                  current={roi.revenue}
                  previous={previousRoi.revenue}
                  label={compareLabel}
                />
              ) : null}
            </Kpi>
            <Kpi label="ROI" value={loadingRoi ? '…' : roi ? formatRoi(roi.roi) : '–'}>
              {!loadingRoi && roi ? (
                <span className="block text-faint text-xs">
                  earned per {formatMoney(1, roi.currency)} spent on sending
                </span>
              ) : null}
              {!loadingRoi && roi && previousRoi ? (
                // No comparison when either period spent nothing (ROI "–").
                <ComparisonBadge
                  current={roi.roi ?? 0}
                  previous={roi.roi === null ? 0 : (previousRoi.roi ?? 0)}
                  label={compareLabel}
                />
              ) : null}
            </Kpi>
          </KpiFrame>
        </div>
      </section>

      <section aria-labelledby="latest-heading">
        <h2 id="latest-heading" className="m-0 mb-3 text-base font-semibold">
          Contacts and latest campaign
        </h2>
        <KpiFrame cols={3}>
          <Kpi
            label="Active contacts"
            value={contactsQuery.data ? formatNumber(contactsQuery.data.total) : '…'}
          >
            <span className="text-faint text-xs">Live count</span>
          </Kpi>
          <Kpi
            label="Click rate · latest"
            value={latest ? formatPercent(latest.clicked / Math.max(1, latest.delivered)) : '–'}
          >
            <span className="text-faint text-xs">{latest?.label ?? '–'}</span>
          </Kpi>
          <Kpi
            label="Unsubscribes · latest"
            value={
              latest ? formatPercent(latest.unsubscribed / Math.max(1, latest.delivered)) : '–'
            }
          >
            <span className="text-faint text-xs">{latest?.label ?? '–'}</span>
          </Kpi>
        </KpiFrame>
      </section>

      <section aria-labelledby="recent-heading">
        <div className="flex items-center mb-3">
          <h2 id="recent-heading" className="m-0 text-base font-semibold">
            Recent campaigns
          </h2>
          <Link to="/kampagner" className="ml-auto text-[13px] font-semibold">
            See all
          </Link>
        </div>
        <div className="border border-border rounded-xl bg-card overflow-x-auto">
          <div className="min-w-[560px]">
            <div className="flex items-center px-4 py-2.5 border-b border-border text-faint text-xs font-medium">
              <span className="flex-grow min-w-0">Name</span>
              <span className="w-[150px] shrink-0">Status</span>
              <span className="w-[70px] shrink-0 text-right">Clicks</span>
              <span className="w-[70px] shrink-0 text-right">Bounce</span>
              <span className="w-[70px] shrink-0 text-right">Date</span>
            </div>
            {recent.length === 0 ? (
              <div className="px-4 py-6 text-muted text-sm">No campaigns yet.</div>
            ) : (
              recent.map((entry) =>
                entry.kind === 'group' ? (
                  <RecentGroupRow
                    key={`group:${entry.group.name}`}
                    group={entry.group}
                    stats={statsQuery.data?.items}
                  />
                ) : (
                  <RecentCampaignRow
                    key={entry.campaign.id}
                    campaign={entry.campaign}
                    stats={statsQuery.data?.items}
                  />
                ),
              )
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const RECENT_ROW =
  'flex items-center px-4 py-3 border-b border-border-soft last:border-b-0 tabular-nums';

/**
 * The newest send with all its language versions added up: "Loyal 2026 (DE)"
 * and "Loyal 2026 (IT)" count as one send, as on the recent list. Archived
 * campaigns (test sends) are left out.
 */
export function latestSend(
  campaigns: Campaign[],
  stats: CampaignStatsListItem[] | undefined,
): { label: string; delivered: number; clicked: number; unsubscribed: number } | null {
  if (!stats) return null;
  const sent = campaigns.filter((c) => c.archivedAt == null && c.sentAt != null);
  const newest = sent.reduce<Campaign | null>(
    (best, c) => (best == null || (c.sentAt ?? 0) > (best.sentAt ?? 0) ? c : best),
    null,
  );
  if (!newest) return null;
  const name = splitLanguage(newest.name)?.name;
  const members = name ? sent.filter((c) => splitLanguage(c.name)?.name === name) : [newest];
  const ids = new Set(members.map((c) => c.id));
  const rows = stats.filter((s) => ids.has(s.id));
  if (!rows.length) return null;
  const sum = (key: 'delivered' | 'clicked' | 'unsubscribed') =>
    rows.reduce((n, s) => n + s[key], 0);
  return {
    label: members.length > 1 ? `${name} · ${members.length} languages` : newest.name,
    delivered: sum('delivered'),
    clicked: sum('clicked'),
    unsubscribed: sum('unsubscribed'),
  };
}

/** Clicks and bounce for one or more campaigns, summed so a group reads as one send. */
function RecentRates({
  ids,
  stats,
}: {
  ids: string[];
  stats: CampaignStatsListItem[] | undefined;
}) {
  const rows = (stats ?? []).filter((s) => ids.includes(s.id));
  const sum = (key: 'clicked' | 'delivered' | 'bounced' | 'sent') =>
    rows.reduce((n, s) => n + s[key], 0);
  return (
    <>
      <span className="w-[70px] shrink-0 text-right text-muted">
        {rows.length ? formatPercent(sum('clicked') / Math.max(1, sum('delivered'))) : '–'}
      </span>
      <span className="w-[70px] shrink-0 text-right text-muted">
        {rows.length ? formatPercent(sum('bounced') / Math.max(1, sum('sent'))) : '–'}
      </span>
    </>
  );
}

function RecentCampaignRow({
  campaign: c,
  stats,
}: {
  campaign: Campaign;
  stats: CampaignStatsListItem[] | undefined;
}) {
  return (
    <Link to={campaignHref(c)} className={cn(RECENT_ROW, 'no-underline text-text hover:bg-hover')}>
      <span className="flex-grow min-w-0 font-medium truncate pr-3">{c.name}</span>
      <span className="w-[150px] shrink-0">
        <CampaignStatusBadge status={c.status} />
      </span>
      <RecentRates ids={[c.id]} stats={stats} />
      <span className="w-[70px] shrink-0 text-right text-muted">
        {formatRelativeDay(c.sentAt ?? c.createdAt)}
      </span>
    </Link>
  );
}

/** The language versions of one send: one row, each language a link to its version. */
function RecentGroupRow({
  group,
  stats,
}: {
  group: CampaignGroup;
  stats: CampaignStatsListItem[] | undefined;
}) {
  const campaigns = group.members.map((m) => m.campaign);
  const latest = Math.max(...campaigns.map((c) => c.sentAt ?? c.createdAt));
  return (
    <div className={RECENT_ROW}>
      <span className="flex-grow min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1 pr-3">
        <span className="font-medium truncate">{group.name}</span>
        <span className="flex flex-wrap gap-1">
          {group.members.map((m) => (
            <Link
              key={m.campaign.id}
              to={campaignHref(m.campaign)}
              aria-label={`Open ${m.campaign.name}`}
              className="font-mono text-[11px] font-medium leading-none px-1.5 py-1 rounded border border-border text-muted no-underline hover:border-accent hover:text-link"
            >
              {m.language}
            </Link>
          ))}
        </span>
      </span>
      <span className="w-[150px] shrink-0">
        <CampaignGroupStatusBadge group={group} />
      </span>
      <RecentRates ids={campaigns.map((c) => c.id)} stats={stats} />
      <span className="w-[70px] shrink-0 text-right text-muted">{formatRelativeDay(latest)}</span>
    </div>
  );
}
